import { useSnackbar } from 'notistack';
import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';
import { fetchContent } from '../../reducers/players/playersThunks';
import StoriesDisplayer from '../../components/storiesDisplayer/StoriesDisplayer';
import NotFound from '../notFound/NotFound';
import { resolvePlayToken } from '../../features/blinklScan/resolvePlayToken';
import { BLINKL_BACKEND_URL } from '../../config/blinkl';

const BlinklScanView = lazy(() => import('../../features/blinklScan/BlinklScanView'));

/**
 * Hosts both phases of the player:
 *  - "play"  : existing flow, ?iv= & ?content= in the URL -> playlist
 *  - "scan"  : Blinkl in-app scan flow, ?orga= & ?campaign= in the URL
 *              On match: fetch the redirect-bearing URL returned by Blinkl,
 *              extract iv/content from the Location header, then render
 *              StoriesDisplayer in place (no page navigation).
 */
const PlayersManager = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();

  const [token, setToken] = useState(null);
  const [scanParams, setScanParams] = useState(null);
  const [scanLayout, setScanLayout] = useState('in');

  const handleFetch = useCallback(
    async (search) => {
      const thunk = await dispatch(
        fetchContent({ iv: search.iv, content: search.content })
      );
      if (thunk.meta.requestStatus === 'rejected') {
        const code = thunk.payload;
        if (code !== 'token-expired' && code !== 'token-error') {
          enqueueSnackbar(t('toasts.getInfosError'), { variant: 'error' });
        }
      }
    },
    [dispatch, enqueueSnackbar, t]
  );

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const iv = search.get('iv');
    const content = search.get('content');
    if (iv && content) {
      const tmp = { iv, content };
      handleFetch(tmp);
      setToken(tmp);
      return;
    }
    const orga = search.get('orga');
    const campaign = search.get('campaign');
    if (orga && campaign) {
      setScanParams({ orga, campaign });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMatch = useCallback(
    async (blinkl) => {
      const url = blinkl && blinkl.data && blinkl.data.url;
      if (!url) {
        enqueueSnackbar(t('scan.matchError'), { variant: 'error' });
        return;
      }
      try {
        const resolved = await resolvePlayToken(url);
        const next = { iv: resolved.iv, content: resolved.content };
        setScanLayout(resolved.layout || 'in');
        setToken(next);
        await handleFetch(next);
      } catch (e) {
        const variant = 'error';
        if (e && e.code === 'link_expired') {
          enqueueSnackbar(t('scan.linkExpired'), { variant });
        } else {
          enqueueSnackbar(t('scan.matchError'), { variant });
        }
      }
    },
    [enqueueSnackbar, handleFetch, t]
  );

  const renderScan = () => {
    if (!scanParams) return <NotFound />;
    if (token) {
      return scanLayout === 'tt' ? (
        <StoriesDisplayer token={token} direction="vertical" />
      ) : (
        <StoriesDisplayer token={token} />
      );
    }
    return (
      <Suspense fallback={null}>
        <BlinklScanView
          orga={scanParams.orga}
          campaign={scanParams.campaign}
          backendUrl={BLINKL_BACKEND_URL}
          onMatch={handleMatch}
        />
      </Suspense>
    );
  };

  return (
    <Routes>
      <Route path="in" element={<StoriesDisplayer token={token} />} />
      <Route path="tt" element={<StoriesDisplayer token={token} direction="vertical" />} />
      <Route path="scan" element={renderScan()} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default PlayersManager;